import { useEffect, useState } from 'react';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import type { DashboardGroupView, SecretaryDashboardView, SupportedLocale } from '@stm/contracts';
import { translate, type TranslationKey } from '@stm/i18n';
import { Link as RouterLink } from 'react-router-dom';
import { ApiRequestError, apiRequest } from './api';

export interface SecretaryDashboardProps {
  locale: SupportedLocale;
  organizationId: string;
}

function GroupBlock(props: {
  locale: SupportedLocale;
  titleKey: TranslationKey;
  group: DashboardGroupView;
}): React.JSX.Element {
  const { locale, titleKey, group } = props;
  return (
    <Paper variant="outlined" sx={{ padding: 2.5 }}>
      <Typography
        variant="h4"
        component="p"
        sx={{
          mb: 1,
          color: group.count > 0 ? 'primary.main' : 'text.secondary',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {group.count}
      </Typography>
      <Typography variant="subtitle1" component="h3">
        {translate(locale, titleKey)}
      </Typography>
      {group.athletes.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          {group.athletes.map((athlete) => `${athlete.familyName} ${athlete.givenName}`).join(', ')}
          {group.count > group.athletes.length ? ' …' : ''}
        </Typography>
      )}
    </Paper>
  );
}

export function SecretaryDashboard({
  locale,
  organizationId,
}: SecretaryDashboardProps): React.JSX.Element {
  const [view, setView] = useState<SecretaryDashboardView | null>(null);
  const [errorKey, setErrorKey] = useState<ApiRequestError['messageKey'] | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiRequest<SecretaryDashboardView>(
      `/api/v1/organizations/${organizationId}/dashboards/secretary`,
    )
      .then((loaded) => {
        if (!cancelled) setView(loaded);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  return (
    <Box component="section" aria-labelledby="secretary-dashboard-title" sx={{ mb: 3 }}>
      <Typography variant="h6" component="h2" id="secretary-dashboard-title" gutterBottom>
        {translate(locale, 'dashboard.secretary.title')}
      </Typography>
      {errorKey !== null && <Alert severity="error">{translate(locale, errorKey)}</Alert>}
      {view === null && errorKey === null && (
        <Typography>{translate(locale, 'dashboard.loading')}</Typography>
      )}
      {view !== null && (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 2,
              mb: 2,
            }}
          >
            <GroupBlock
              locale={locale}
              titleKey="dashboard.missingGuardianLinks"
              group={view.missingGuardianLinks}
            />
            <Paper variant="outlined" sx={{ padding: 2.5 }}>
              <Typography
                variant="h4"
                component="p"
                sx={{ mb: 1, color: 'primary.main', fontVariantNumeric: 'tabular-nums' }}
              >
                {view.pendingGuardianLinks}
              </Typography>
              <Typography variant="subtitle1" component="h3">
                {translate(locale, 'dashboard.pendingGuardianLinks')}
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ padding: 2.5 }}>
              <Typography
                variant="h4"
                component="p"
                sx={{ mb: 1, color: 'primary.main', fontVariantNumeric: 'tabular-nums' }}
              >
                {view.pendingImports}
              </Typography>
              <Typography variant="subtitle1" component="h3">
                {translate(locale, 'dashboard.pendingImports')}
              </Typography>
            </Paper>
            <GroupBlock locale={locale} titleKey="dashboard.newJoiners" group={view.newJoiners} />
            <GroupBlock
              locale={locale}
              titleKey="dashboard.inactiveAthletes"
              group={view.inactiveAthletes}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {translate(locale, 'dashboard.evidenceNote')}
          </Typography>
          <Button component={RouterLink} to="/athletes" variant="outlined" sx={{ mt: 2 }}>
            {translate(locale, 'dashboard.openAthletes')}
          </Button>
        </>
      )}
    </Box>
  );
}
